import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Spinner, Switch } from '@nextui-org/react';
import { useNavigate } from 'react-router-dom';
import es from '../../../renderer/i18n/locales/es.json';
import { apiFetch, getApiBase } from '../lib/api';
import { LiveFestModel, LiveFestView } from '../lib/agencyLiveFest';
import { useAuth } from '../hooks/useAuth';

// Agencias → LIVE FEST: la sección «LIVE FEST» del menú lateral de la consola
// de agencias (debajo de una línea, tras Tickets). Aquí se enciende o se
// apaga y se pone la fecha de su cuenta atrás; la pantalla es un «lo estamos
// preparando» con el rótulo «LIVE FEST 26». A la derecha, la misma pantalla
// que pinta la app (lib/agencyLiveFest.js importa sus archivos).
// Worker: cf-worker/src/api/agencyLiveFest.js (D1 app_config['agency_live_fest']).

const ENDPOINT = '/api/backoffice/agency-live-fest';
const M = LiveFestModel;

const LANGS = [
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English' }
];

const pad = (n) => String(n).padStart(2, '0');
// ms → valor de <input type="datetime-local"> (hora de este navegador).
function toInput(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Valor del campo → ms (hora de este navegador), o null.
function fromInput(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

const zoneName = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { return ''; }
})();

// Un 404 es que el Worker que contesta no tiene todavía esta pantalla (sin
// desplegar, o el backoffice en local yendo al de producción: lib/api.js).
function explain(error, doing) {
  const text = String(error?.message || '');
  if (!/^404\b/.test(text)) return `${doing} (${text || 'error'}).`;
  const host = getApiBase().replace(/^https?:\/\//, '');
  return `${doing}: el Worker que ha contestado (${host}) no tiene todavía LIVE FEST. Hay que desplegarlo; ` +
    'si estás probando en local, comprueba que tu Worker local está arrancado y recarga la página.';
}

function when(ms) {
  return ms ? new Date(ms).toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

function Section({ title, hint, children, action }) {
  return (
    <section className="space-y-3 rounded-xl border border-default-200 bg-content1 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          {hint ? <p className="text-sm text-default-500">{hint}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

export default function AgenciaLiveFest() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [form, setForm] = useState(null);           // { enabled, startsAt }
  const [saved, setSaved] = useState(null);         // { json, updatedAt }
  const [lang, setLang] = useState('es');
  const [dict, setDict] = useState({ es });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);     // { type: 'ok' | 'error', text }
  const previewRef = useRef(null);

  const apply = useCallback((data) => {
    const next = M.sanitize(data.config);
    setForm(next);
    setSaved({ json: JSON.stringify(next), updatedAt: data.updatedAt || null });
  }, []);

  useEffect(() => {
    let alive = true;
    apiFetch(ENDPOINT, { cache: 'no-store' })
      .then((data) => { if (alive) apply(data); })
      .catch((e) => {
        if (!alive) return;
        apply({ config: null });
        setMessage({ type: 'error', text: explain(e, 'No se pudo leer lo guardado') });
      });
    return () => { alive = false; };
  }, [apply]);

  useEffect(() => {
    if (lang !== 'en' || dict.en) return;
    let alive = true;
    import('../../../renderer/i18n/locales/en.json')
      .then((mod) => { if (alive) setDict((d) => ({ ...d, en: mod.default })); })
      .catch(() => { if (alive) setDict((d) => ({ ...d, en: {} })); });
    return () => { alive = false; };
  }, [lang, dict.en]);

  // Vista previa: la pantalla de la app con lo que hay en el formulario.
  useEffect(() => {
    const host = previewRef.current;
    if (!host || !form) return undefined;
    const strings = dict[lang] || {};
    const t = (key, fallback) => strings[key] || fallback;
    const view = LiveFestView.mount(host, { config: form, t, locale: lang === 'es' ? 'es-ES' : 'en-US' });
    return () => view.destroy();
  }, [form, lang, dict]);

  const dirty = useMemo(() => !!form && !!saved && JSON.stringify(M.sanitize(form)) !== saved.json, [form, saved]);

  if (!form) return <div className="flex justify-center p-8"><Spinner /></div>;

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      const data = await apiFetch(ENDPOINT, { method: 'PUT', body: { config: M.sanitize(form) } });
      apply(data);
      const on = !!(data.config && data.config.enabled);
      setMessage({ type: 'ok', text: on ? 'Guardado: LIVE FEST sale en la consola de las agencias en un par de minutos.' : 'Guardado: LIVE FEST no sale en la consola.' });
    } catch (e) {
      setMessage({ type: 'error', text: explain(e, 'No se pudo guardar') });
    } finally {
      setBusy(false);
    }
  }

  const tone = { ok: 'text-success', error: 'text-danger' };
  const past = form.startsAt != null && form.startsAt <= Date.now();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-3xl">
          <button className="text-sm text-default-500 hover:underline" onClick={() => navigate('/agencias/consulta')}>← Agencias</button>
          <h1 className="text-2xl font-semibold">LIVE FEST</h1>
          <p className="text-sm text-default-500">
            La sección «LIVE FEST» del menú lateral de la consola de agencias: va debajo de una línea, tras Tickets, y la ve todo el
            que entra en la consola de una agencia. De momento es una pantalla de «lo estamos preparando» con la cuenta atrás hasta la
            fecha que pongas aquí; sin fecha, dice «Muy pronto».
          </p>
          {saved?.updatedAt ? <p className="text-xs text-default-400">Último cambio: {when(saved.updatedAt)}</p> : null}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-default-200 p-1" role="group" aria-label="Idioma de la vista previa">
          {LANGS.map((l) => (
            <Button key={l.id} size="sm" variant={lang === l.id ? 'solid' : 'light'} color={lang === l.id ? 'primary' : 'default'} onPress={() => setLang(l.id)}>
              {l.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(340px,420px)_minmax(0,1fr)]">
        <div className="order-2 space-y-4 xl:order-1">
          <Section
            title="Mostrar en la consola"
            hint="Encendida, las agencias ven «LIVE FEST» en su menú lateral. Apagada, no sale (quien la tenga abierta vuelve al Inicio)."
            action={<Switch isSelected={form.enabled} isDisabled={!canWrite} onValueChange={(enabled) => setForm((f) => ({ ...f, enabled }))} aria-label="Mostrar LIVE FEST en la consola" />}
          >
            <p className={`text-sm ${form.enabled ? 'text-success' : 'text-default-500'}`}>{form.enabled ? 'Encendida' : 'Apagada'}</p>
          </Section>

          <Section
            title="Cuenta atrás"
            hint={`Fecha y hora a la que llega a cero, en la hora de este navegador${zoneName ? ` (${zoneName})` : ''}. Cada agencia la ve en su hora.`}
          >
            <div className="flex flex-wrap items-end gap-2">
              <Input
                size="sm" variant="bordered" type="datetime-local" aria-label="Fecha y hora de la cuenta atrás" isReadOnly={!canWrite}
                className="max-w-xs" value={toInput(form.startsAt)}
                onValueChange={(v) => setForm((f) => ({ ...f, startsAt: fromInput(v) }))}
              />
              {canWrite && form.startsAt != null ? (
                <Button size="sm" variant="light" onPress={() => setForm((f) => ({ ...f, startsAt: null }))}>Quitar fecha</Button>
              ) : null}
            </div>
            {form.startsAt == null ? <p className="text-xs text-default-400">Sin fecha: en lugar de la cuenta atrás sale «Muy pronto».</p> : null}
            {past ? <p className="text-xs text-warning">Esa fecha ya ha pasado: en lugar de la cuenta atrás sale «Ya casi está».</p> : null}
          </Section>

          <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-default-200 bg-content1 p-3">
            {canWrite ? (
              <>
                <Button color="primary" isLoading={busy} isDisabled={!dirty} onPress={save}>Guardar</Button>
                <Button variant="light" isDisabled={busy || !dirty} onPress={() => { setForm(JSON.parse(saved.json)); setMessage(null); }}>Descartar cambios</Button>
              </>
            ) : <span className="text-sm text-default-500">Solo lectura.</span>}
            {message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : (dirty ? <p className="text-sm text-warning">Cambios sin guardar.</p> : null)}
          </div>
        </div>

        <div className="order-1 space-y-2 xl:sticky xl:top-0 xl:order-2">
          <h2 className="text-base font-semibold">Así se ve en la consola</h2>
          <div className="alf-preview overflow-hidden rounded-xl border border-default-200 bg-black p-3">
            <div ref={previewRef} />
          </div>
          <p className="text-xs text-default-400">La letra del rótulo es TikTok Sans. Se ve aunque esté apagada, para poder prepararla antes.</p>
        </div>
      </div>
    </div>
  );
}
